import { useCallback, useEffect, useRef, useState } from 'react';
import * as Y from 'yjs';
import { canvasRoots } from '@/features/canvas/engine/wiring/canvas-roots';
import { useCanvasBinding } from '@/features/canvas/engine/wiring/contexts/canvas-binding-context';

export interface UseTentativeBatchArgs {
  doc: Y.Doc;
  clientId: number;
}

function collectTentativeRealIds(nodes: Y.Map<Y.Map<unknown>>): string[] {
  const ids: string[] = [];
  nodes.forEach((node, id) => {
    if (!(node instanceof Y.Map)) return;
    if (node.get('type') !== 'entity-proxy') return;
    const data = node.get('data');
    if (!(data instanceof Y.Map)) return;
    if (data.get('tentative') === true && data.get('isVirtual') === false) {
      ids.push(id);
    }
  });
  return ids;
}

/**
 * Tracks tentative-real entity-proxy nodes (tentative=true, isVirtual=false)
 * on the canvas and provides an acceptAll callback that flips them all to
 * confirmed (tentative=false) in a single Yjs transact.
 *
 * Per client.md §16: visible whenever count > 0.
 * The count is recomputed O(n) on each Yjs node Map change — fine for
 * canvases with hundreds of nodes.
 */
export function useTentativeBatch({ doc, clientId }: UseTentativeBatchArgs) {
  const binding = useCanvasBinding();
  const nodes = canvasRoots(doc, binding).nodes;
  const [count, setCount] = useState<number>(
    () => collectTentativeRealIds(nodes).length,
  );

  // Recompute the *current* set inside acceptAll via this ref, so the
  // callback keeps a stable identity but always sees fresh ids. This avoids
  // useEffect-wrapped consumers re-firing every observe event when count
  // changes (which would happen if `ids` were a useCallback dep).
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;

  useEffect(() => {
    const recompute = () => setCount(collectTentativeRealIds(nodes).length);
    nodes.observeDeep(recompute);
    return () => nodes.unobserveDeep(recompute);
  }, [nodes]);

  const acceptAll = useCallback(() => {
    const ids = collectTentativeRealIds(nodesRef.current);
    if (ids.length === 0) return;
    doc.transact(() => {
      for (const id of ids) {
        const node = nodesRef.current.get(id);
        if (!(node instanceof Y.Map)) continue;
        const data = node.get('data');
        if (!(data instanceof Y.Map)) continue;
        data.set('tentative', false);
      }
    }, clientId);
  }, [doc, clientId]);

  return { count, acceptAll };
}
